import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogOut, User, Info, Shield, FileText, Phone, Mail, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LEGAL_LINKS, supportTelHref, supportMailtoHref } from "@/lib/legalLinks";
import { AISettingsCard } from "@/components/AISettingsCard";
import { BlockedSendersCard } from "@/components/BlockedSendersCard";

export default function VendorSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const openExternal = (url) => {
    // Opens outside the WebView on native; new tab on web.
    window.open(url, "_blank", "noreferrer");
  };

  const openDeleteAccount = () => {
    const url = `${window.location.origin}${LEGAL_LINKS.deleteAccountPath}`;
    window.open(url, "_blank", "noreferrer");
  };

  return (
    <div className="space-y-4" data-testid="settings-page">
      <h2 className="text-xl font-heading font-bold text-zinc-900">Settings</h2>

      <Card className="border border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center">
              <User className="w-5 h-5 text-zinc-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-900">{user?.email}</p>
              <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start h-11 text-sm"
              onClick={() => navigate("/vendor/profile")} data-testid="go-to-profile">
              <User className="w-4 h-4 mr-3" /> Edit Profile
            </Button>
          </div>
        </CardContent>
      </Card>

      <AISettingsCard />
      <BlockedSendersCard />

      {/* Item 8 — Legal + Support */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-heading font-semibold text-zinc-900">Legal &amp; Support</h3>
          </div>
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start h-11 text-sm"
                    onClick={() => openExternal(LEGAL_LINKS.privacyUrl)}
                    data-testid="settings-privacy-link">
              <Shield className="w-4 h-4 mr-3" /> Privacy Policy
            </Button>
            <Button variant="outline" className="w-full justify-start h-11 text-sm"
                    onClick={() => openExternal(LEGAL_LINKS.termsUrl)}
                    data-testid="settings-terms-link">
              <FileText className="w-4 h-4 mr-3" /> Terms of Service
            </Button>
            <a href={supportTelHref()}
               className="flex items-center gap-3 h-11 px-3 rounded-md border border-zinc-200 hover:bg-zinc-50 text-sm"
               data-testid="settings-support-phone">
              <Phone className="w-4 h-4 text-zinc-500" />
              <span className="text-zinc-900">Call support — {LEGAL_LINKS.supportPhone}</span>
            </a>
            <a href={supportMailtoHref()}
               className="flex items-center gap-3 h-11 px-3 rounded-md border border-zinc-200 hover:bg-zinc-50 text-sm"
               data-testid="settings-support-email">
              <Mail className="w-4 h-4 text-zinc-500" />
              <span className="text-zinc-900 break-all">{LEGAL_LINKS.supportEmail}</span>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Item 6 — Request account deletion */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-heading font-semibold text-zinc-900">Account</h3>
          </div>
          <Button variant="outline"
                  className="w-full justify-start h-11 text-sm border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  onClick={openDeleteAccount}
                  data-testid="settings-request-deletion">
            <Trash2 className="w-4 h-4 mr-3" /> Request account deletion
          </Button>
          <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
            Opens a form at apexinfinityventures.com. Our team reviews each request within 2 business days.
          </p>
        </CardContent>
      </Card>

      <Card className="border border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-heading font-semibold text-zinc-900">About</h3>
          </div>
          <div className="space-y-2 text-xs text-zinc-500">
            <div className="flex justify-between"><span>App</span><span className="text-zinc-900">APEX FieldProIQ</span></div>
            <div className="flex justify-between"><span>Version</span><span className="text-zinc-900">1.0.0</span></div>
            <div className="flex justify-between"><span>Portal</span><span className="text-zinc-900">Vendor</span></div>
          </div>
        </CardContent>
      </Card>

      <Button variant="outline"
        className="w-full h-11 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
        onClick={handleLogout}
        data-testid="settings-logout-button">
        <LogOut className="w-4 h-4 mr-2" /> Sign Out
      </Button>
    </div>
  );
}
